import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import api, { auth } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Stamp, FileText, ShieldCheck, ArrowRight, Settings, UserCheck, Activity, Search, Package, Layers } from "lucide-react";
import { WebAuthnEnrolPanel } from "@/components/WebAuthn";
import PendingBadge from "@/components/PendingBadge";
import MirrorFreshness from "@/components/MirrorFreshness";
import UserMenu from "@/components/UserMenu";

const REVIEW_ENABLED = process.env.REACT_APP_ENABLE_REQUESTOR_REVIEW === "true";

export default function Home() {
  const user = auth.user();
  const nav = useNavigate();
  const role = user?.role || "";
  // Auto-detect approval access from MS SQL. Start optimistic for users whose
  // last-known cached flag was true, then let /me/access correct it within ~500ms.
  // This prevents the dashboard tile from disappearing for a moment after a
  // refresh — annoying for active approvers like the admin who hits this many
  // times a day. Strict non-approvers (cached flag = false OR no role label)
  // start with the tile hidden.
  const cachedFlag = user?.has_approval_access;
  const optimisticDefault =
    cachedFlag === true ||
    (cachedFlag === undefined && ["Authoriser", "Approver", "Both", "Admin"].includes(role));
  const [canApprove, setCanApprove] = useState(optimisticDefault);
  const canCreate = !!user;
  // Poll mirror status every 30s so the freshness pill on Home reflects the
  // live sync state even if the user never opens the dashboard.
  const [syncInfo, setSyncInfo] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const r = await api.get("/mirror/status");
        if (!cancelled) setSyncInfo(r.data);
      } catch (err) {
        // Non-fatal — mirror status is just a UX hint
        console.warn("[Home] /mirror/status fetch failed", err);
      }
    };
    fetchStatus();
    const id = setInterval(fetchStatus, 30_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await auth.myAccess();
        if (cancelled) return;
        setCanApprove(!!data.has_approval_access);
        // Cache the flag so the ApproverOnly route guard can read it synchronously.
        try {
          const u = JSON.parse(localStorage.getItem("pr_user") || "{}");
          u.has_approval_access = !!data.has_approval_access;
          localStorage.setItem("pr_user", JSON.stringify(u));
        } catch (err) {
          console.warn("[Home] failed to cache has_approval_access in localStorage", err);
        }
      } catch (err) {
        // Keep optimistic default — backend may be cold; the route guard
        // will re-check on first protected navigation.
        console.warn("[Home] /auth/my-access fetch failed", err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-cyan-50/40 to-slate-50">
      <header className="h-16 bg-cyan-700 text-white sticky top-0 z-40">
        <div className="max-w-[1200px] mx-auto h-full px-3 sm:px-6 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
              <ShieldCheck size={18} />
            </div>
            <div>
              <div className="text-sm sm:text-base font-bold tracking-tight leading-none">Procurement Suite</div>
              <MirrorFreshness syncInfo={syncInfo} compact />
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <PendingBadge />
            {role === "Admin" && (
              <Button variant="ghost" size="sm" onClick={() => nav("/admin")} className="text-white hover:bg-white/10" data-testid="home-admin">
                <Settings size={16} className="sm:mr-1" /><span className="hidden sm:inline">Admin</span>
              </Button>
            )}
            <UserMenu tone="dark" />
          </div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-4 py-10 sm:py-16">
        <div className="text-center mb-10 sm:mb-14">
          <div className="text-xs sm:text-sm tracking-[0.3em] uppercase text-cyan-700 font-semibold">Welcome back</div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 mt-2">Hi {user?.full_name?.split(" ")[0] || "there"} — pick your workspace</h1>
          <p className="text-slate-600 mt-3 text-sm sm:text-base max-w-xl mx-auto">
            Your role <span className="font-semibold text-cyan-800">{role || "User"}</span> gives you access to the workspaces below. You can switch between them anytime from the header.
          </p>
        </div>

        {REVIEW_ENABLED && canCreate && (
          <div className="mb-8 sm:mb-10 max-w-3xl mx-auto">
            <button
              onClick={() => nav("/pr-review")}
              data-testid="ws-card-review"
              className="group w-full text-left bg-white border-2 border-amber-300 hover:border-amber-500 hover:shadow-lg rounded-sm p-5 sm:p-6 transition-all flex items-center gap-4"
            >
              <div className="w-12 h-12 rounded-sm bg-amber-100 text-amber-800 flex items-center justify-center group-hover:bg-amber-600 group-hover:text-white transition-colors flex-shrink-0">
                <UserCheck size={22} />
              </div>
              <div className="flex-1">
                <div className="text-xs uppercase tracking-wider text-amber-700 font-bold mb-0.5">New · Verify PRs in your name</div>
                <h2 className="text-base sm:text-lg font-bold text-slate-900">Spot bogus PRs before they hit Authoriser</h2>
                <p className="text-xs sm:text-sm text-slate-600 mt-1">
                  Quickly Approve or Reject Open PRs raised in your name. Rejections are stamped with a reason and blocked from the L1 queue.
                </p>
              </div>
              <ArrowRight size={18} className="text-amber-700 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        )}

        <div className={`grid grid-cols-1 ${canApprove ? "lg:grid-cols-2" : ""} gap-4 sm:gap-6 max-w-3xl mx-auto`}>
          {/* Approvals card — only shown for users with approval/authorisation access */}
          {canApprove && (
            <button
              onClick={() => nav("/")}
              data-testid="ws-card-approvals"
              className="group relative text-left bg-white border-2 border-slate-200 hover:border-cyan-600 hover:shadow-xl hover:-translate-y-0.5 cursor-pointer rounded-sm p-6 sm:p-7 transition-all"
            >
              <div className="w-12 h-12 rounded-sm bg-cyan-100 text-cyan-800 flex items-center justify-center mb-4 group-hover:bg-cyan-700 group-hover:text-white transition-colors">
                <Stamp size={22} />
              </div>
              <div className="text-xs uppercase tracking-wider text-cyan-700 font-bold mb-1">Approval workspace</div>
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900">PR Approval Dashboard</h2>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                Review, authorise and approve incoming PRs with deterministic AI risk scoring across price &amp; vendor signals.
              </p>
              <div className="mt-5 inline-flex items-center text-sm font-semibold text-cyan-800 group-hover:translate-x-1 transition-transform">
                Enter dashboard <ArrowRight size={16} className="ml-1" />
              </div>
            </button>
          )}

          {/* Creation card */}
          <button
            onClick={() => canCreate && nav("/my-prs")}
            disabled={!canCreate}
            data-testid="ws-card-creation"
            className={`group relative text-left bg-white border-2 rounded-sm p-6 sm:p-7 transition-all ${canCreate ? "border-slate-200 hover:border-cyan-600 hover:shadow-xl hover:-translate-y-0.5 cursor-pointer" : "border-slate-100 opacity-50 cursor-not-allowed"}`}
          >
            <div className="w-12 h-12 rounded-sm bg-emerald-100 text-emerald-800 flex items-center justify-center mb-4 group-hover:bg-emerald-700 group-hover:text-white transition-colors">
              <FileText size={22} />
            </div>
            <div className="text-xs uppercase tracking-wider text-emerald-700 font-bold mb-1">Creation workspace</div>
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900">My PRs &amp; New Requests</h2>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">
              Draft new purchase requisitions with live AI pre-checks, track your submissions and see their progress through Authorise → Approve.
            </p>
            <div className={`mt-5 inline-flex items-center text-sm font-semibold ${canCreate ? "text-emerald-800 group-hover:translate-x-1 transition-transform" : "text-slate-400"}`}>
              {canCreate ? "Open my workspace" : "Not available"} {canCreate && <ArrowRight size={16} className="ml-1" />}
            </div>
          </button>
        </div>

        <div className="text-center mt-12 text-xs text-slate-500">
          Logged in as <span className="font-semibold text-slate-700">{user?.username}</span> · Emp #{user?.emp_no}
        </div>

        {/* PR Pipeline — open to every user. Lets anyone track where a PR sits
            (Requestor Review / L1 / L2 / Hold / Done) without needing approval
            rights. Read-only. */}
        <div className="max-w-3xl mx-auto mt-8">
          <button
            onClick={() => nav("/pipeline")}
            data-testid="ws-card-pipeline"
            className="group w-full text-left bg-white border-2 border-slate-200 hover:border-cyan-600 hover:shadow-lg rounded-sm p-5 sm:p-6 transition-all flex items-center gap-4"
          >
            <div className="w-12 h-12 rounded-sm bg-cyan-100 text-cyan-800 flex items-center justify-center group-hover:bg-cyan-700 group-hover:text-white transition-colors flex-shrink-0">
              <Activity size={22} />
            </div>
            <div className="flex-1">
              <div className="text-xs uppercase tracking-wider text-cyan-700 font-bold mb-0.5">Track any PR · open to all users</div>
              <h2 className="text-base sm:text-lg font-bold text-slate-900">PR Pipeline</h2>
              <p className="text-xs sm:text-sm text-slate-600 mt-1">
                Live cross-stage view of every PR — Requestor Review, L1, L2, Hold, Completed. Search by PR number or requestor.
              </p>
            </div>
            <ArrowRight size={18} className="text-cyan-700 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>

        {/* Price Explorer — read-only search for any item's lowest & last cost
            across vendors. Useful for requestors before raising a PR and for
            approvers doing a quick sanity check on a line item's rate. */}
        <div className="max-w-3xl mx-auto mt-4">
          <button
            onClick={() => nav("/price-explorer")}
            data-testid="ws-card-price-explorer"
            className="group w-full text-left bg-white border-2 border-slate-200 hover:border-emerald-600 hover:shadow-lg rounded-sm p-5 sm:p-6 transition-all flex items-center gap-4"
          >
            <div className="w-12 h-12 rounded-sm bg-emerald-100 text-emerald-800 flex items-center justify-center group-hover:bg-emerald-700 group-hover:text-white transition-colors flex-shrink-0">
              <Search size={22} />
            </div>
            <div className="flex-1">
              <div className="text-xs uppercase tracking-wider text-emerald-700 font-bold mb-0.5">Look up any item · open to all users</div>
              <h2 className="text-base sm:text-lg font-bold text-slate-900">Price Explorer</h2>
              <p className="text-xs sm:text-sm text-slate-600 mt-1">
                Search by item code or name — see the lowest and last billed cost across every vendor in the last 180 days.
              </p>
            </div>
            <ArrowRight size={18} className="text-emerald-700 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>

        {/* SAP Workspace — one entry point for every SAP-adjacent module (MRP,
            List Prices, Data Mirror, Buyer Assignments, integration
            health). ERP-style page with 4 dropdown menus. */}
        <div className="max-w-3xl mx-auto mt-4">
          <button
            onClick={() => nav("/sap")}
            data-testid="ws-card-sap-workspace"
            className="group w-full text-left bg-white border-2 border-slate-200 hover:border-cyan-600 hover:shadow-lg rounded-sm p-5 sm:p-6 transition-all flex items-center gap-4"
          >
            <div className="w-12 h-12 rounded-sm bg-cyan-100 text-cyan-800 flex items-center justify-center group-hover:bg-cyan-700 group-hover:text-white transition-colors flex-shrink-0">
              <Layers size={22}/>
            </div>
            <div className="flex-1">
              <div className="text-xs uppercase tracking-wider text-cyan-700 font-bold mb-0.5">SAP Business ByDesign · integration console</div>
              <h2 className="text-base sm:text-lg font-bold text-slate-900">SAP Workspace</h2>
              <p className="text-xs sm:text-sm text-slate-600 mt-1">
                MRP Proposals · List Prices · Master Data · Approvals · Broker health — all in one ERP-style page.
              </p>
            </div>
            <ArrowRight size={18} className="text-cyan-700 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>

        <div className="max-w-3xl mx-auto mt-8">
          <WebAuthnEnrolPanel />
        </div>
      </main>
    </div>
  );
}
